import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {Ban} from 'lucide-react';
import '../Games.css';
import {Player} from '../common/types';
import {loadJSON, saveJSON} from '../common/storage';
import PlayerSetup from '../common/PlayerSetup';
import SingleReveal from '../common/SingleReveal';
import RoundTimer from '../common/RoundTimer';
import CategoryChips from '../common/CategoryChips';
import {useSwipe} from '../common/useSwipe';
import {BASE_WORDS, CUSTOM_CATEGORY, TabooItem} from './tabooWords';

const MIN_PLAYERS = 3;
const PLAYERS_KEY = 'games_players';
const CUSTOM_WORDS_KEY = 'taboo_custom_words';
const DISABLED_CATEGORIES_KEY = 'taboo_disabled_categories';
const ROUND_OPTIONS = [45, 60, 90, 120];

type Stage = 'setup' | 'handoff' | 'playing' | 'roundEnd';

const TabooCard: React.FC<{word: TabooItem}> = ({word}) => (
    <>
        <p className="GmRoleLocation">{word.text}</p>
        <div className="GmTabooForbidden">
            <p className="GmTabooForbidden-label"><Ban size={14}/> Нельзя говорить</p>
            <div className="GmTabooForbidden-list">
                {word.forbidden.map(w => (
                    <span className="GmTabooForbidden-item" key={w}>{w}</span>
                ))}
            </div>
        </div>
    </>
);

const TabooGame: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>(() => loadJSON(PLAYERS_KEY, []));
    const [customWords, setCustomWords] = useState<TabooItem[]>(() => loadJSON(CUSTOM_WORDS_KEY, []));
    const [disabledCategories, setDisabledCategories] = useState<string[]>(() => loadJSON(DISABLED_CATEGORIES_KEY, []));
    const [newWordText, setNewWordText] = useState('');
    const [newForbiddenText, setNewForbiddenText] = useState('');
    const [roundSeconds, setRoundSeconds] = useState(60);

    const [stage, setStage] = useState<Stage>('setup');
    const [presenterIndex, setPresenterIndex] = useState(0);
    const [currentWord, setCurrentWord] = useState<TabooItem | null>(null);
    const [usedWords, setUsedWords] = useState<Set<string>>(new Set());
    const [lastResult, setLastResult] = useState<boolean | null>(null);
    const [tally, setTally] = useState({correct: 0, missed: 0});

    const allWords = useMemo(() => [...BASE_WORDS, ...customWords], [customWords]);
    const categories = useMemo(() => {
        const set = new Set(allWords.map(w => w.category));
        return Array.from(set);
    }, [allWords]);
    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        allWords.forEach(w => {
            counts[w.category] = (counts[w.category] || 0) + 1;
        });
        return counts;
    }, [allWords]);
    const activeWords = useMemo(
        () => allWords.filter(w => !disabledCategories.includes(w.category)),
        [allWords, disabledCategories]
    );

    const updatePlayers = (next: Player[]) => {
        setPlayers(next);
        saveJSON(PLAYERS_KEY, next);
    };

    const toggleCategory = (category: string) => {
        const next = disabledCategories.includes(category)
            ? disabledCategories.filter(c => c !== category)
            : [...disabledCategories, category];
        setDisabledCategories(next);
        saveJSON(DISABLED_CATEGORIES_KEY, next);
    };

    const addCustomWord = () => {
        const text = newWordText.trim();
        if (!text) return;
        const forbidden = newForbiddenText.split(',').map(w => w.trim()).filter(Boolean);
        const next = [...customWords, {text, forbidden, category: CUSTOM_CATEGORY, custom: true}];
        setCustomWords(next);
        saveJSON(CUSTOM_WORDS_KEY, next);
        setNewWordText('');
        setNewForbiddenText('');
    };

    const removeCustomWord = (text: string) => {
        const next = customWords.filter(w => w.text !== text);
        setCustomWords(next);
        saveJSON(CUSTOM_WORDS_KEY, next);
    };

    const pickWord = (pool: TabooItem[], used: Set<string>): {word: TabooItem; nextUsed: Set<string>} => {
        let available = pool.filter(w => !used.has(w.text));
        let nextUsed = used;
        if (available.length === 0) {
            available = pool;
            nextUsed = new Set();
        }
        const word = available[Math.floor(Math.random() * available.length)];
        const updatedUsed = new Set(nextUsed);
        updatedUsed.add(word.text);
        return {word, nextUsed: updatedUsed};
    };

    const startGame = () => {
        if (players.length < MIN_PLAYERS || activeWords.length === 0) return;
        const {word, nextUsed} = pickWord(activeWords, new Set());
        setPresenterIndex(0);
        setCurrentWord(word);
        setUsedWords(nextUsed);
        setTally({correct: 0, missed: 0});
        setStage('handoff');
    };

    const finishRound = (success: boolean) => {
        setLastResult(success);
        setTally(t => success ? {...t, correct: t.correct + 1} : {...t, missed: t.missed + 1});
        setStage('roundEnd');
    };

    const swipe = useSwipe({
        onSwipeRight: () => finishRound(true),
        onSwipeLeft: () => finishRound(false),
        threshold: 90,
    });

    const nextRound = () => {
        const {word, nextUsed} = pickWord(activeWords, usedWords);
        setCurrentWord(word);
        setUsedWords(nextUsed);
        setPresenterIndex(i => (i + 1) % players.length);
        setStage('handoff');
    };

    const backToSetup = () => {
        setStage('setup');
        setCurrentWord(null);
    };

    return (
        <div className={`GmPage ${stage === 'setup' ? '' : 'GmPage--noBar'}`}>
            <div className="GmContainer">
                <div className="GmTopBar">
                    <Link to="/games" className="GmBack">← Игры</Link>
                    <h1 className="GmTitle" style={{margin: 0}}>Табу</h1>
                </div>

                {stage === 'setup' && (
                    <>
                        <p className="GmSubtitle">
                            Игрок объясняет слово остальным, не называя его и слова из запретного списка.
                            Сказал запрещённое слово — раунд не засчитан. Угадали до истечения времени — успех.
                        </p>

                        <PlayerSetup players={players} setPlayers={updatePlayers} minPlayers={MIN_PLAYERS}/>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Время на раунд</h3>
                            <div className="GmBtnRow">
                                {ROUND_OPTIONS.map(sec => (
                                    <button
                                        key={sec}
                                        className={`GmBtn ${roundSeconds === sec ? 'GmBtn--primary' : ''}`}
                                        onClick={() => setRoundSeconds(sec)}
                                    >{sec} сек</button>
                                ))}
                            </div>
                        </div>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Категории ({activeWords.length} слов активно)</h3>
                            <CategoryChips
                                categories={categories}
                                counts={categoryCounts}
                                disabled={disabledCategories}
                                onToggle={toggleCategory}
                            />

                            {customWords.length > 0 && (
                                <div className="GmList" style={{marginBottom: 12}}>
                                    {customWords.map(w => (
                                        <div className="GmListItem" key={w.text}>
                                            <span className="GmListItem-name">{w.text}</span>
                                            <button className="GmIconBtn" onClick={() => removeCustomWord(w.text)} aria-label="Удалить">✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="GmInputRow" style={{flexDirection: 'column', gap: 8}}>
                                <input
                                    className="GmInput"
                                    placeholder="Своё слово"
                                    value={newWordText}
                                    onChange={(e) => setNewWordText(e.target.value)}
                                />
                                <input
                                    className="GmInput"
                                    placeholder="Запрещённые слова через запятую"
                                    value={newForbiddenText}
                                    onChange={(e) => setNewForbiddenText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') addCustomWord();
                                    }}
                                />
                                <button className="GmBtn" onClick={addCustomWord}>Добавить</button>
                            </div>
                        </div>

                        <div className="GmBottomBar">
                            <div className="GmBottomBar-inner">
                                <button
                                    className="GmBtn GmBtn--primary GmBtn--big"
                                    disabled={players.length < MIN_PLAYERS || activeWords.length === 0}
                                    onClick={startGame}
                                >
                                    Начать игру
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'handoff' && currentWord && (
                    <SingleReveal
                        player={players[presenterIndex]}
                        handoffLabel="Передайте телефон игроку"
                        revealButtonLabel={`Я — ${players[presenterIndex].name}, показать слово`}
                        hideButtonLabel="Готов объяснять"
                        warning="⚠ Не показывайте экран остальным!"
                        onHide={() => setStage('playing')}
                        content={
                            <>
                                <p className="GmHint">{currentWord.category}</p>
                                <TabooCard word={currentWord}/>
                            </>
                        }
                    />
                )}

                {stage === 'playing' && currentWord && (
                    <div
                        className="GmSwipeCard"
                        onTouchStart={swipe.onTouchStart}
                        onTouchMove={swipe.onTouchMove}
                        onTouchEnd={swipe.onTouchEnd}
                        style={swipe.dragging ? {
                            transform: `translateX(${swipe.offsetX}px) rotate(${swipe.offsetX / 20}deg)`,
                            transition: 'none',
                        } : undefined}
                    >
                        <span
                            className="GmSwipeCard-badge GmSwipeCard-badge--right"
                            style={{opacity: swipe.dragging ? Math.min(1, Math.max(0, swipe.offsetX) / 90) : 0}}
                        >ДА</span>
                        <span
                            className="GmSwipeCard-badge GmSwipeCard-badge--left"
                            style={{opacity: swipe.dragging ? Math.min(1, Math.max(0, -swipe.offsetX) / 90) : 0}}
                        >МИМО</span>

                        <h3 className="GmCard-title">{players[presenterIndex].name} объясняет</h3>
                        <RoundTimer seconds={roundSeconds} onExpire={() => finishRound(false)}/>
                        <TabooCard word={currentWord}/>
                        <p className="GmSwipeHint">← смахни, если не угадали · угадали — смахни вправо →</p>
                        <div className="GmBtnRow" style={{marginTop: 12, justifyContent: 'center'}}>
                            <button className="GmBtn GmBtn--primary" onClick={() => finishRound(true)}>Угадали ✅</button>
                            <button className="GmBtn" onClick={() => finishRound(false)}>Не угадали ⏭</button>
                        </div>
                    </div>
                )}

                {stage === 'roundEnd' && currentWord && (
                    <div className="GmCard" style={{textAlign: 'center'}}>
                        <h3 className="GmCard-title">{lastResult ? 'Угадали! 🎉' : 'Не угадали'}</h3>
                        <p>Слово было: <strong>{currentWord.text}</strong></p>
                        <p className="GmHint">Счёт: {tally.correct} угадано, {tally.missed} не угадано</p>
                        <div className="GmBtnRow" style={{marginTop: 16, justifyContent: 'center'}}>
                            <button className="GmBtn GmBtn--primary" onClick={nextRound}>Следующий раунд</button>
                            <button className="GmBtn" onClick={backToSetup}>Закончить игру</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TabooGame;
